import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { imageUrl } from "@/components/ImageUpload";
import { Globe, MapPin, Gift, Sparkles, Star, Calendar, BadgeCheck, Trophy } from "lucide-react";
import { VendorTrustRow } from "@/pages/Vendors";
import { SEOHead, JsonLd, buildAggregateRating } from "@/components/SEO";

// 24-hex ObjectId → use legacy /vendors/{id} -> /vendors/{slug} 301-style redirect.
const isObjectId = (s) => typeof s === "string" && /^[0-9a-f]{24}$/i.test(s);

// Mirrors backend `seo_utils.slugify` — kebab-case ASCII, used only for
// cross-link URL construction below. Stays in lockstep with the backend slug
// function (no accents in our category/city data so the parity is exact).
const slugify = (s) =>
    (s || "").toString().toLowerCase().normalize("NFKD")
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "") || "x";

export default function VendorDetail() {
    const { slugOrId } = useParams();
    const navigate = useNavigate();
    const [v, setV] = useState(null);
    const [cats, setCats] = useState([]);
    const [boothElements, setBoothElements] = useState([]);
    const [reviewList, setReviewList] = useState([]);
    const [eventsAttended, setEventsAttended] = useState([]);
    const [related, setRelated] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Slug lookup is preferred; fall back to id for legacy URLs.
                const lookup = isObjectId(slugOrId)
                    ? api.get(`/vendors/${slugOrId}`)
                    : api.get(`/vendors/by-slug/${slugOrId}`);
                const vr = await lookup;
                if (cancelled) return;
                const vendor = vr.data;
                // 301-style redirect: if a legacy id URL has a slug available,
                // swap the address bar so search engines see canonical slug URLs.
                if (isObjectId(slugOrId) && vendor.vendor_slug && vendor.vendor_slug !== slugOrId) {
                    navigate(`/vendors/${vendor.vendor_slug}`, { replace: true });
                    return;
                }
                setV(vendor);

                const [cr, be, rv, att, rel] = await Promise.all([
                    api.get("/categories"),
                    api.get("/booth-elements"),
                    api.get(`/vendors/${vendor.id}/reviews`),
                    api.get(`/vendors/${vendor.id}/events-attended`),
                    api.get(`/vendors/${vendor.id}/related`),
                ]);
                if (cancelled) return;
                setCats(cr.data);
                setBoothElements(be.data);
                setReviewList(rv.data);
                setEventsAttended(att.data);
                setRelated(rel.data);
            } catch (err) {
                if (err?.response?.status === 404) setNotFound(true);
            }
        })();
        return () => { cancelled = true; };
    }, [slugOrId, navigate]);

    if (notFound) {
        return (
            <Shell>
                <SEOHead title="Vendor not found" noindex />
                <div className="max-w-2xl mx-auto px-6 py-24 text-center">
                    <h1 className="font-heading text-3xl font-extrabold">Vendor not found</h1>
                    <p className="text-brand-mute mt-3">This vendor may have been removed or the link is incorrect.</p>
                    <Link to="/vendors" className="pill btn-forest px-5 py-3 font-semibold inline-block mt-6">Browse all vendors</Link>
                </div>
            </Shell>
        );
    }
    if (!v) return <Shell><div className="p-10 text-brand-mute">Loading…</div></Shell>;

    const catName = (cid) => cats.find((c) => c.id === cid)?.name || cid;
    const photos = v.photos || [];
    // Legacy data may persist `interactive_booth_elements` as a list of ids;
    // the backend migrates on startup but coerce defensively for read paths.
    const _ibe = v.interactive_booth_elements;
    const ibeNames = Array.isArray(_ibe)
        ? _ibe.map((eid) => boothElements.find((el) => el.id === eid)?.name).filter(Boolean)
        : (typeof _ibe === "string" && _ibe.trim()
            ? _ibe.split(",").map((s) => s.trim()).filter(Boolean)
            : []);
    const trust = v.trust;
    const showRating = trust?.rating?.show_public_rating;
    const primaryCatName = catName(v.primary_category_id || (v.categories || [])[0]);
    const cityState = [v.home_city, v.home_state].filter(Boolean).join(", ") || v.service_area || "";

    // --- SEO copy (template-driven, deterministic per vendor) -------------
    const seoTitle = `${v.business_name}${primaryCatName ? ` — ${primaryCatName}` : ""}${cityState ? ` in ${cityState}` : ""}`;
    const fairsLine = trust?.health_fairs_attended
        ? `Attended ${trust.health_fairs_attended} HealthFairX health fair${trust.health_fairs_attended === 1 ? "" : "s"}.`
        : "";
    const ratingLine = showRating
        ? ` ${trust.rating.avg_stars}★ from ${trust.rating.approved_count} verified host review${trust.rating.approved_count === 1 ? "" : "s"}.`
        : "";
    const seoDescription = `${v.business_name}${primaryCatName ? ` is a ${primaryCatName.toLowerCase()} vendor` : ""}${cityState ? ` serving ${cityState}-area corporate health fairs` : ""}. ${fairsLine}${ratingLine} Book them through HealthFairX.`.trim();
    const ogImage = photos[0] ? imageUrl(photos[0]) : (v.logo_url ? imageUrl(v.logo_url) : undefined);

    // --- JSON-LD: LocalBusiness + AggregateRating + Reviews ----------------
    const aggregate = buildAggregateRating(trust);
    const ldReviews = (reviewList || []).slice(0, 10).map((r) => ({
        "@type": "Review",
        reviewRating: { "@type": "Rating", ratingValue: r.stars, bestRating: 5, worstRating: 1 },
        author: { "@type": "Organization", name: r.event?.name || "Host" },
        datePublished: r.event?.event_date,
        reviewBody: r.text || (r.tags || []).join(", "),
    }));
    const localBusiness = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: v.business_name,
        description: v.description || seoDescription,
        url: typeof window !== "undefined" ? window.location.href : undefined,
        image: ogImage,
        ...(cityState && {
            address: {
                "@type": "PostalAddress",
                addressLocality: v.home_city,
                addressRegion: v.home_state,
                addressCountry: v.home_country || "US",
            },
        }),
        ...(v.website && { sameAs: [v.website] }),
        ...(aggregate && { aggregateRating: aggregate }),
        ...(ldReviews.length > 0 && { review: ldReviews }),
    };
    const breadcrumb = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: typeof window !== "undefined" ? window.location.origin : "" },
            { "@type": "ListItem", position: 2, name: "Vendors", item: typeof window !== "undefined" ? `${window.location.origin}/vendors` : "" },
            { "@type": "ListItem", position: 3, name: v.business_name },
        ],
    };

    return (
        <Shell>
            <SEOHead title={seoTitle} description={seoDescription} image={ogImage} type="profile" />
            <JsonLd data={localBusiness} />
            <JsonLd data={breadcrumb} />

            <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
                {/* Cover photo */}
                {photos[0] && (
                    <div className="rounded-3xl overflow-hidden border border-brand-line mb-8 aspect-[21/9] bg-brand-bone">
                        <img src={imageUrl(photos[0])} alt={`${v.business_name} booth photo`} className="w-full h-full object-cover" />
                    </div>
                )}

                <div className="rounded-3xl border border-brand-line bg-white p-10 grid md:grid-cols-3 gap-8">
                    <div>
                        <div className="h-28 w-28 rounded-2xl bg-brand-mint inline-flex items-center justify-center font-heading text-4xl font-extrabold text-brand-forest overflow-hidden">
                            {v.logo_url ? (
                                <img src={imageUrl(v.logo_url)} alt={`${v.business_name} logo`} className="w-full h-full object-cover" />
                            ) : (
                                <span>{(v.business_name || "?").slice(0, 1).toUpperCase()}</span>
                            )}
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <h1 className="font-heading text-3xl md:text-4xl font-extrabold">{v.business_name}</h1>
                        {primaryCatName && (
                            <div className="mt-1 text-sm font-semibold text-brand-forest" data-testid="vendor-primary-category">{primaryCatName}</div>
                        )}
                        <div className="mt-3 flex flex-wrap gap-3 text-sm text-brand-mute">
                            {v.service_area && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {v.service_area}</span>}
                            {v.website && <a href={v.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-brand-forest"><Globe size={14} /> Website</a>}
                        </div>
                        <p className="mt-4 text-brand-ink/80 leading-relaxed">{v.description || "—"}</p>
                        <div className="flex flex-wrap gap-1.5 mt-5">
                            {(v.categories || []).map((cid) => <span key={cid} className="chip">{catName(cid)}</span>)}
                        </div>
                        {v.freebies && (
                            <div className="mt-6 rounded-xl bg-brand-bone p-4 flex items-start gap-3" data-testid="vendor-detail-freebies">
                                <Gift size={18} className="text-brand-coral mt-0.5" />
                                <div>
                                    <div className="font-bold">Booth Freebies</div>
                                    <div className="text-sm text-brand-mute">{v.freebies}</div>
                                </div>
                            </div>
                        )}
                        {v.raffle_prize && (
                            <div className="mt-4 rounded-xl bg-brand-coral/10 p-4 flex items-start gap-3" data-testid="vendor-detail-raffle-prize">
                                <Trophy size={18} className="text-brand-coral mt-0.5" />
                                <div>
                                    <div className="font-bold">Raffle prize</div>
                                    <div className="text-xs text-brand-mute mt-0.5">Larger-ticket prize attendees can enter to win at the booth.</div>
                                    <div className="text-sm text-brand-ink mt-1.5 font-semibold">{v.raffle_prize}</div>
                                </div>
                            </div>
                        )}
                        {ibeNames.length > 0 && (
                            <div className="mt-4 rounded-xl bg-brand-mint/40 p-4 flex items-start gap-3" data-testid="vendor-detail-ibe">
                                <Sparkles size={18} className="text-brand-forest mt-0.5" />
                                <div className="min-w-0">
                                    <div className="font-bold">Interactive booth elements</div>
                                    <div className="text-xs text-brand-mute mt-0.5">What employees can experience at this booth</div>
                                    <div className="flex flex-wrap gap-1.5 mt-3">
                                        {ibeNames.map((name) => (
                                            <span key={name} className="text-xs font-semibold px-3 py-1 rounded-full bg-white border border-brand-forest/30 text-brand-forest">{name}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        <VendorTrustRow trust={trust} />
                        {showRating && trust.rating.top_tags.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="vendor-detail-top-tags">
                                {trust.rating.top_tags.map((t) => (
                                    <span key={t} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-mint text-brand-forest">{t}</span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Health Fair Experience — past confirmed events */}
                {eventsAttended.length > 0 && (
                    <section className="mt-12" data-testid="vendor-detail-experience">
                        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                            <BadgeCheck size={20} className="text-brand-forest" /> Health fair experience
                        </h2>
                        <p className="text-sm text-brand-mute mt-1">
                            HealthFairX events {v.business_name} has participated in.
                        </p>
                        <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                            {eventsAttended.map((ev) => (
                                <li key={ev.event_id} className="rounded-xl border border-brand-line bg-white p-4 flex items-start gap-3" data-testid={`experience-${ev.event_id}`}>
                                    <Calendar size={16} className="text-brand-forest mt-0.5 shrink-0" />
                                    <div className="min-w-0">
                                        <Link to={`/events/${ev.event_slug || ev.event_id}`} className="font-semibold hover:underline truncate block">{ev.name}</Link>
                                        <div className="text-xs text-brand-mute mt-0.5">
                                            {ev.event_date}{ev.city ? ` · ${ev.city}${ev.state ? `, ${ev.state}` : ""}` : ""}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Reviews section — only approved reviews */}
                {reviewList.length > 0 && (
                    <section className="mt-12">
                        <h2 className="font-heading text-2xl font-bold">What hosts say</h2>
                        <div className="mt-4 space-y-4" data-testid="vendor-detail-reviews">
                            {reviewList.map((r) => (
                                <div key={r.id} className="rounded-xl border border-brand-line bg-white p-5" data-testid={`review-${r.id}`}>
                                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                        <div className="flex items-center gap-1 text-amber-500">
                                            {Array.from({ length: r.stars }).map((_, i) => (<Star key={i} size={14} className="fill-amber-400" />))}
                                            {Array.from({ length: 5 - r.stars }).map((_, i) => (<Star key={`e${i}`} size={14} className="text-brand-line" />))}
                                        </div>
                                        <div className="text-xs text-brand-mute">{r.event?.name} • {r.event?.event_date}</div>
                                    </div>
                                    {r.text && <p className="text-sm text-brand-ink/85 mt-3 whitespace-pre-line">{r.text}</p>}
                                    {(r.tags || []).length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-3">
                                            {r.tags.map((t) => <span key={t} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-brand-bone text-brand-ink">{t}</span>)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {photos.length > 1 && (
                    <section className="mt-12">
                        <div className="label-base">Booth photos</div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
                            {photos.slice(1).map((p) => (
                                <div key={p} className="rounded-xl border border-brand-line overflow-hidden aspect-[4/3] bg-brand-bone">
                                    <img src={imageUrl(p)} alt={`${v.business_name} booth`} className="w-full h-full object-cover" />
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Related vendors — internal linking goldmine */}
                {related.length > 0 && (
                    <section className="mt-16 pt-10 border-t border-brand-line" data-testid="vendor-detail-related">
                        <h2 className="font-heading text-2xl font-bold">Similar vendors</h2>
                        <p className="text-sm text-brand-mute mt-1">
                            Other healthcare and wellness vendors{primaryCatName ? ` in ${primaryCatName.toLowerCase()}` : ""}{cityState ? ` near ${cityState}` : ""}.
                        </p>
                        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {related.map((r) => (
                                <Link key={r.id} to={`/vendors/${r.vendor_slug || r.id}`} data-testid={`related-${r.id}`}
                                      className="rounded-xl border border-brand-line bg-white p-4 card-hover block">
                                    <div className="flex items-center gap-3">
                                        <div className="h-12 w-12 rounded-xl bg-brand-mint flex items-center justify-center overflow-hidden shrink-0">
                                            {r.logo_url ? (
                                                <img src={imageUrl(r.logo_url)} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="font-heading font-extrabold text-brand-forest">{(r.business_name || "?").slice(0, 1).toUpperCase()}</span>
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-semibold truncate">{r.business_name}</div>
                                            <div className="text-xs text-brand-mute truncate">
                                                {r.service_area || [r.home_city, r.home_state].filter(Boolean).join(", ") || "—"}
                                            </div>
                                        </div>
                                    </div>
                                    <VendorTrustRow trust={r.trust} />
                                </Link>
                            ))}
                        </div>
                        {/* Cross-links to category + city landing pages (and the combined page). */}
                        <div className="mt-8 flex flex-wrap gap-2" data-testid="vendor-detail-seo-links">
                            {primaryCatName && (
                                <Link to={`/vendors/${slugify(primaryCatName)}`} className="pill px-4 py-2 text-sm font-semibold bg-white border border-brand-line hover:border-brand-forest hover:bg-brand-mint">
                                    All {primaryCatName.toLowerCase()} vendors →
                                </Link>
                            )}
                            {cityState && (
                                <Link to={`/vendors/${slugify(cityState)}`} className="pill px-4 py-2 text-sm font-semibold bg-white border border-brand-line hover:border-brand-forest hover:bg-brand-mint">
                                    All vendors in {cityState} →
                                </Link>
                            )}
                            {primaryCatName && cityState && (
                                <Link to={`/vendors/${slugify(cityState)}/${slugify(primaryCatName)}`} className="pill px-4 py-2 text-sm font-semibold bg-brand-forest text-white hover:bg-brand-forest/90">
                                    {primaryCatName} in {cityState} →
                                </Link>
                            )}
                        </div>
                    </section>
                )}
            </div>
        </Shell>
    );
}
